import React from "react";
import Select from "react-select";
import { X } from "lucide-react";

const UserModal = ({
  showModal,
  setShowModal,
  resetUserForm,
  isEditingUser,
  activeTab,
  userForm,
  handleUserFormChange,
  handleEmailChange,
  emailError,
  selectedDepartment,
  setSelectedDepartment,
  selectedUserDivision,
  setSelectedUserDivision,
  departmentOptions,
  divisionOptions,
  handleCreateUser,
  handleUpdateUser,
  isCreatingUser,
  isUpdatingUser,
  hasUserChanges,
}) => {
  if (!showModal) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => {
          setShowModal(false);
          resetUserForm();
        }}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEditingUser ? "Edit User" : `Add ${activeTab}`}
          </h2>
          <button
            onClick={() => {
              setShowModal(false);
              resetUserForm();
            }}
            className="p-1 rounded-md hover:bg-gray-100 transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Name
            </label>
            <input
              type="text"
              placeholder="Enter name"
              value={userForm.name}
              onChange={(e) => handleUserFormChange("name", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Employee ID
            </label>
            <input
              type="text"
              placeholder="Enter employee ID"
              value={userForm.employeeId}
              onChange={(e) =>
                handleUserFormChange("employeeId", e.target.value)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
              autoComplete="off"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              placeholder="Enter email"
              value={userForm.email}
              onChange={(e) => handleEmailChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
              autoComplete="off"
            />
            {emailError && (
              <p className="text-red-500 text-sm mt-1">{emailError}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              type="password"
              placeholder="Enter password"
              value={userForm.password}
              onChange={(e) => handleUserFormChange("password", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Department
            </label>
            <Select
              value={selectedDepartment}
              isClearable
              isSearchable={false}
              options={departmentOptions}
              placeholder="Select Department"
              onChange={setSelectedDepartment}
              className="text-sm"
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: "40px",
                  border: "1px solid #d1d5db",
                  borderRadius: "6px",
                  "&:hover": {
                    borderColor: "#9ca3af",
                  },
                }),
              }}
              required
            />
          </div>

          {(activeTab === "Supervisor" || activeTab === "Technician") && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Division
              </label>
              <Select
                value={selectedUserDivision}
                isClearable
                isSearchable={false}
                options={divisionOptions}
                placeholder={
                  selectedDepartment
                    ? "Select Division"
                    : "Select Department first"
                }
                onChange={setSelectedUserDivision}
                isDisabled={!selectedDepartment}
                className="text-sm"
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: "40px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    "&:hover": {
                      borderColor: "#9ca3af",
                    },
                  }),
                }}
                required
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={() => {
              setShowModal(false);
              resetUserForm();
            }}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={isEditingUser ? handleUpdateUser : handleCreateUser}
            disabled={
              isCreatingUser ||
              isUpdatingUser ||
              (isEditingUser && !hasUserChanges)
            }
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isCreatingUser
              ? "Adding..."
              : isUpdatingUser
              ? "Updating..."
              : isEditingUser
              ? "Update"
              : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserModal;
